import { ValueCard } from '@/components/Base/ValueCard/ValueCard'
import { AxiomReportType } from '@/enums/axiom-report.enum'
import {
  IReturnVarianceReportOverviewOthers,
  IReturnVarianceReportOverviewTotalReturns,
} from '@/interfaces/rvReport.interface'
import { getReturnVarianceReportOverview } from '@/services/axiomReport.service'
import { getColumnDetail } from '@/utils/grid-utils/columnUtils'
import { formatCellValue } from '@/utils/tableGridUtils'
import { formatNumber } from '@/utils/utils'
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material'
import React, { useEffect, useState } from 'react'

export const ReturnVarianceReportOverview: React.FC<{
  reportId: number
  reportType: string
}> = ({ reportId, reportType }) => {
  const [overviewTotalReturn, setOverviewTotalReturn] =
    useState<IReturnVarianceReportOverviewTotalReturns>()
  const [overviewOthers, setOverviewOthers] =
    useState<IReturnVarianceReportOverviewOthers>()
  const [loaderCard, setLoaderCard] = useState<boolean>(false)

  const fetchRvOverview = async () => {
    try {
      setLoaderCard(true)
      const res = await getReturnVarianceReportOverview(reportId)
      if (res) {
        const { total_ret_p, total_ret_b, total_ret_d } = res
        setOverviewTotalReturn({ total_ret_p, total_ret_b, total_ret_d })
        const { allocation, selection, currency } = res
        setOverviewOthers({ allocation, selection, currency })
      }
    } catch (error) {
    } finally {
      setLoaderCard(false)
    }
  }

  useEffect(() => {
    fetchRvOverview()
  }, [reportId])

  return (
    <Box
      display='grid'
      gridTemplateColumns='repeat(3, 1fr)'
      gridTemplateRows='auto auto'
      gap={4}
      minWidth='20rem'
    >
      {loaderCard ? (
        <Box
          display='grid'
          height='100%'
          alignItems='center'
          justifyContent='center'
          role='loading'
          gridColumn='1 / 4'
          gridRow='1 / 3'
        >
          <CircularProgress color='primary' />
        </Box>
      ) : (
        <>
          <Card
            sx={{
              gridColumn: '1 / span 3',
            }}
          >
            <CardHeader
              title={<Typography variant='h3'>Total Return</Typography>}
            />
            <CardContent>
              {overviewTotalReturn && (
                <Stack direction='row' gap={9} px={9} alignItems='center'>
                  {Object.keys(overviewTotalReturn)?.map(
                    (
                      key:
                        | keyof IReturnVarianceReportOverviewTotalReturns
                        | string,
                    ) => {
                      const colDetail = getColumnDetail(key, AxiomReportType.RV)
                      const value =
                        overviewTotalReturn[
                          key as keyof IReturnVarianceReportOverviewTotalReturns
                        ]
                      const label = colDetail?.header
                      const displayValue = formatCellValue(
                        formatNumber(value, true, 2, 2),
                        colDetail?.format,
                      )
                      return (
                        <ValueCard
                          key={key}
                          label={label}
                          value={value}
                          displayValue={displayValue}
                          reportType={reportType}
                        />
                      )
                    },
                  )}
                </Stack>
              )}
            </CardContent>
          </Card>
          <>
            {overviewOthers &&
              Object.keys(overviewOthers)?.map(
                (key: keyof IReturnVarianceReportOverviewOthers | string) => {
                  const colDetail = getColumnDetail(key, AxiomReportType.RV)
                  const value =
                    overviewOthers[
                      key as keyof IReturnVarianceReportOverviewOthers
                    ]
                  const label = colDetail?.header
                  const displayValue = formatCellValue(
                    formatNumber(value, true, 2, 2),
                    colDetail?.format,
                  )
                  return (
                    <Card key={key}>
                      <CardHeader
                        title={<Typography variant='h3'>{label}</Typography>}
                      />
                      <CardContent sx={{ pt: 0 }}>
                        <Box
                          display='grid'
                          alignItems='center'
                          justifyContent='center'
                          sx={{ aspectRatio: 2 / 1 }}
                        >
                          <Typography
                            variant='body1'
                            fontSize={'2.25rem'}
                            color='text.primary'
                            fontWeight='700'
                          >
                            {displayValue}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  )
                },
              )}
          </>
        </>
      )}
    </Box>
  )
}
